import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faDownload,
  faEye,
  faLink,
  faRefresh,
  faSearch,
  faTimes,
  faUpload,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {
  getSettlementPdfRegenerationBatchHistory,
  refreshSettlementPdfRegenerationBatchSignedUrl,
} from "../../services/settlementApi";

const SettlementPdfRegenerationBatchHistoryPage = () => {
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [uploader, setUploader] = useState("");
  const [appliedUploader, setAppliedUploader] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [pagination, setPagination] = useState({});
  const [refreshingId, setRefreshingId] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSettlementPdfRegenerationBatchHistory({
        page,
        limit: perPage,
        search: appliedSearch,
        status,
        uploader: appliedUploader,
      });
      setUploads(response.data.data || []);
      setPagination(response.data.pagination || {});
    } catch (error) {
      toast.error("Failed to fetch upload history.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, appliedSearch, status, appliedUploader]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSearch = () => {
    setAppliedSearch(search);
    setAppliedUploader(uploader);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setUploader("");
    setAppliedUploader("");
    setStatus("");
    setPage(1);
  };

  const handleDownloadFile = (upload) => {
    if (!upload.signed_url) {
      toast.warning("No download link available. Try refreshing the URL first.");
      return;
    }
    window.open(upload.signed_url, "_blank", "noopener,noreferrer");
  };

  const handleRefreshUrl = async (upload) => {
    setRefreshingId(upload.upload_id);
    try {
      const response = await refreshSettlementPdfRegenerationBatchSignedUrl(upload.upload_id);
      const result = response.data;
      toast.success(result.message || "Signed URL refreshed.");
      setUploads((prev) =>
        prev.map((u) =>
          u.upload_id === upload.upload_id
            ? { ...u, signed_url: result.signed_url, url_expires_at: result.expires_at }
            : u
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to refresh URL.");
    } finally {
      setRefreshingId(null);
    }
  };

  const getStatusBadge = (value) => {
    const map = {
      uploaded: { bg: "bg-blue-100", text: "text-blue-800", label: "Uploaded" },
      processing: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Processing" },
      completed: { bg: "bg-green-100", text: "text-green-800", label: "Completed" },
      failed: { bg: "bg-red-100", text: "text-red-800", label: "Failed" },
    };
    const badge = map[value] || map.uploaded;
    return <span className={`px-2.5 py-1 rounded text-xs font-semibold ${badge.bg} ${badge.text}`}>{badge.label}</span>;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const hasActiveFilters = appliedSearch || appliedUploader || status;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Batch Regenerate PIS/Form 2307 History</h1>
            <p className="text-gray-600">View all PRN payload uploads and regeneration status.</p>
          </div>
          <button
            onClick={() => navigate("/batch-regenerate-pis-form2307")}
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg"
          >
            <FontAwesomeIcon icon={faUpload} />
            New Upload
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-3 mb-3">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Search by File Name / Upload ID</label>
              <input
                type="text"
                placeholder="File name or upload ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                <FontAwesomeIcon icon={faUser} className="text-gray-500" />
                Filter by Uploader
              </label>
              <input
                type="text"
                placeholder="Uploader name or username..."
                value={uploader}
                onChange={(e) => setUploader(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
              >
                <FontAwesomeIcon icon={faSearch} /> Search
              </button>
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg flex items-center gap-1.5 text-sm"
                  title="Clear filters"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
              <button
                onClick={fetchHistory}
                disabled={loading}
                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded-lg flex items-center gap-2 text-sm"
                title="Refresh"
              >
                <FontAwesomeIcon icon={faRefresh} className={loading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {["", "uploaded", "processing", "completed", "failed"].map((item) => (
              <button
                key={item}
                onClick={() => {
                  setStatus(item);
                  setPage(1);
                }}
                className={`px-3 py-1 rounded text-xs font-medium capitalize ${
                  status === item ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {item === "" ? "All Status" : item}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">File Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Uploaded By</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Total</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Success</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Failed</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Size</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Uploaded</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                  </tr>
                ) : uploads.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-8 text-center text-gray-500">No uploads found</td>
                  </tr>
                ) : (
                  uploads.map((upload) => (
                    <tr key={upload.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-800 max-w-xs">
                        <p className="font-medium truncate text-sm">{upload.original_file_name}</p>
                        <p className="text-xs text-gray-400 font-mono truncate">{upload.upload_id}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-700 text-sm">
                        <div className="flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faUser} className="text-gray-400 text-xs" />
                          <span>{upload.uploaded_by_name || "-"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(upload.status)}</td>
                      <td className="px-4 py-3 text-center font-semibold text-gray-800">{upload.total_rows || 0}</td>
                      <td className="px-4 py-3 text-center font-semibold text-green-700">{upload.success_count || 0}</td>
                      <td className="px-4 py-3 text-center font-semibold text-red-700">{upload.failed_count || 0}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{formatFileSize(upload.file_size)}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(upload.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                          <button
                            onClick={() => navigate(`/batch-regenerate-pis-form2307-processed/${upload.upload_id}`)}
                            disabled={upload.status === "uploaded" || upload.status === "processing"}
                            className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded text-xs font-medium flex items-center gap-1.5"
                            title="View processed rows"
                          >
                            <FontAwesomeIcon icon={faEye} /> View
                          </button>
                          <button
                            onClick={() => handleDownloadFile(upload)}
                            disabled={!upload.signed_url}
                            className="px-2.5 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded text-xs font-medium flex items-center gap-1.5"
                            title="Download original payload"
                          >
                            <FontAwesomeIcon icon={faDownload} /> Download
                          </button>
                          <button
                            onClick={() => handleRefreshUrl(upload)}
                            disabled={refreshingId === upload.upload_id}
                            className="px-2.5 py-1.5 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white rounded text-xs font-medium flex items-center gap-1.5"
                            title="Refresh signed download URL"
                          >
                            <FontAwesomeIcon icon={faLink} className={refreshingId === upload.upload_id ? "animate-spin" : ""} />
                            {refreshingId === upload.upload_id ? "Refreshing..." : "Refresh URL"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {uploads.length === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
              {Math.min(page * perPage, pagination.total || 0)} of {pagination.total || 0} uploads
            </div>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || loading}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-sm font-medium text-gray-700">
                Page {pagination.current_page || 1} of {pagination.total_pages || 1}
              </span>
              <button
                onClick={() => setPage(Math.min(pagination.total_pages || 1, page + 1))}
                disabled={page >= (pagination.total_pages || 1) || loading}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default SettlementPdfRegenerationBatchHistoryPage;
